import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Chip,
    Stack,
    Alert,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme as useMuiTheme,
    Divider,
    Grid,
    Avatar,
    Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Work as WorkIcon,
    School as SchoolIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    fetchJobApplicants,
    deleteJobApplicant,
    updateJobApplicantStatus,
    setFilters,
    clearFilters,
    fetchDesignations
} from '../../store/slices/jobApplicantSlice';
import { JobApplicant } from '../../store/slices/jobApplicantSlice';
import { useTheme } from '../../contexts/ThemeContext';
import JobApplicantForm from './JobApplicantForm';
import JobApplicantView from './JobApplicantView';
import ConfirmationDialog from '../../components/ConfirmationDialog';

const JobApplicants: React.FC = () => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const dispatch = useAppDispatch();
    
    const { applicants, pagination, filters, designations, loading, error } = useAppSelector((state) => state.jobApplicants);

    // Local state
    const [searchQuery, setSearchQuery] = useState(filters.query);
    const [selectedDesignation, setSelectedDesignation] = useState(filters.designation);
    const [selectedStatus, setSelectedStatus] = useState(filters.status);
    const [currentPage, setCurrentPage] = useState(1);
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [selectedApplicant, setSelectedApplicant] = useState<JobApplicant | null>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [applicantToDelete, setApplicantToDelete] = useState<JobApplicant | null>(null);

    // Load job applicants on component mount
    useEffect(() => {
        loadJobApplicants();
        dispatch(fetchDesignations());
    }, [currentPage, filters]);

    const loadJobApplicants = () => {
        const params = {
            page: currentPage,
            per_page: 20,
            ...filters
        };
        dispatch(fetchJobApplicants(params));
    };

    // Handle search
    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setCurrentPage(1);
    };

    // Handle filter changes
    const handleDesignationChange = (event: any) => {
        const value = event.target.value;
        setSelectedDesignation(value);
        dispatch(setFilters({ designation: value }));
        setCurrentPage(1);
    };

    const handleStatusChange = (event: any) => {
        const value = event.target.value;
        setSelectedStatus(value);
        dispatch(setFilters({ status: value }));
        setCurrentPage(1);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedDesignation('');
        setSelectedStatus('');
        dispatch(clearFilters());
        setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Handle applicant actions
    const handleViewApplicant = (applicant: JobApplicant) => {
        setSelectedApplicant(applicant);
        setOpenView(true);
    };

    const handleDeleteApplicant = (applicant: JobApplicant) => {
        setApplicantToDelete(applicant);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (applicantToDelete) {
            await dispatch(deleteJobApplicant(applicantToDelete.id));
            setDeleteDialog(false);
            setApplicantToDelete(null);
            loadJobApplicants();
        }
    };

    const handleStatusUpdate = async (applicant: JobApplicant, newStatus: string) => {
        await dispatch(updateJobApplicantStatus({ id: applicant.id, status: newStatus }));
        loadJobApplicants();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusChip = (status: string) => {
        const statusConfig = {
            pending: { color: 'warning' as const, label: 'Pending' },
            shortlisted: { color: 'info' as const, label: 'Shortlisted' },
            rejected: { color: 'error' as const, label: 'Rejected' },
            hired: { color: 'success' as const, label: 'Hired' }
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="outlined"
            />
        );
    };

    const handleDownloadResume = (resumeUrl: string, applicantName: string) => {
        const link = document.createElement('a');
        link.href = resumeUrl;
        link.download = `${applicantName}_resume.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 2 : 0
            }}>
                <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    sx={{ 
                        color: theme.palette.text.primary, 
                        fontWeight: 600,
                        textAlign: isMobile ? 'center' : 'left'
                    }}
                >
                    Job Applicants
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters Card */}
            <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: theme.palette.background.paper }}>
                <CardContent sx={{ p: isExtraSmall ? 1 : isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Filters
                    </Typography>
                    <Grid container spacing={isMobile ? 2 : 2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search by name or email"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton onClick={handleSearch}>
                                            <SearchIcon />
                                        </IconButton>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Designation</InputLabel>
                                <Select
                                    value={selectedDesignation}
                                    onChange={handleDesignationChange}
                                    label="Designation"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Designations</MenuItem>
                                    {designations.map((designation) => (
                                        <MenuItem key={designation} value={designation}>
                                            {designation}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    label="Status"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="shortlisted">Shortlisted</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                    <MenuItem value="hired">Hired</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={{
                                    borderRadius: 3,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Desktop Table */}
            {!isMobile && (
                <Card sx={{ bgcolor: theme.palette.background.paper }}>
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Experience</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Applied Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : applicants.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No job applicants found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        applicants.map((applicant) => (
                                            <TableRow key={applicant.id} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                                            <PersonIcon fontSize="small" />
                                                        </Avatar>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {applicant.name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <EmailIcon fontSize="small" color="action" />
                                                            <Typography variant="body2">{applicant.email}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PhoneIcon fontSize="small" color="action" />
                                                            <Typography variant="body2">{applicant.phone}</Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <WorkIcon fontSize="small" color="action" />
                                                        <Typography variant="body2">{applicant.designation}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{applicant.experience}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                                        <Select
                                                            value={applicant.status}
                                                            onChange={(e) => handleStatusUpdate(applicant, e.target.value)}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 3,
                                                                    '& fieldset': {
                                                                        borderWidth: '1px',
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <MenuItem value="pending">Pending</MenuItem>
                                                            <MenuItem value="shortlisted">Shortlisted</MenuItem>
                                                            <MenuItem value="rejected">Rejected</MenuItem>
                                                            <MenuItem value="hired">Hired</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{formatDate(applicant.created_at)}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewApplicant(applicant)}
                                                                sx={{ color: theme.palette.primary.main }}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {applicant.resume_url && (
                                                            <Tooltip title="Download Resume">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDownloadResume(applicant.resume_url!, applicant.name)}
                                                                    sx={{ color: theme.palette.success.main }}
                                                                >
                                                                    <DownloadIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {/* <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteApplicant(applicant)}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip> */}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* Mobile Cards */}
            {isMobile && (
                <Card sx={{ bgcolor: theme.palette.background.paper }}>
                    <CardContent sx={{ p: isExtraSmall ? 1 : 2 }}>
                        {loading ? (
                            <Typography align="center">Loading...</Typography>
                        ) : applicants.length === 0 ? (
                            <Typography align="center">No job applicants found</Typography>
                        ) : (
                            <Stack spacing={isExtraSmall ? 1 : 2}>
                                {applicants.map((applicant) => (
                                    <Card key={applicant.id} variant="outlined" sx={{ bgcolor: theme.palette.background.default }}>
                                        <CardContent sx={{ p: isExtraSmall ? 1 : 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                                        <PersonIcon fontSize="small" />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            {applicant.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {applicant.designation}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                                    <Select
                                                        value={applicant.status}
                                                        onChange={(e) => handleStatusUpdate(applicant, e.target.value)}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 3,
                                                                '& fieldset': {
                                                                    borderWidth: '1px',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="pending">Pending</MenuItem>
                                                        <MenuItem value="shortlisted">Shortlisted</MenuItem>
                                                        <MenuItem value="rejected">Rejected</MenuItem>
                                                        <MenuItem value="hired">Hired</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                            
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <EmailIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{applicant.email}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <PhoneIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{applicant.phone}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <SchoolIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{applicant.education}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <WorkIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">{applicant.experience}</Typography>
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1 }} />
                                            
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Applied: {formatDate(applicant.created_at)}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewApplicant(applicant)}
                                                        sx={{ color: theme.palette.primary.main }}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                    {applicant.resume_url && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDownloadResume(applicant.resume_url!, applicant.name)}
                                                            sx={{ color: theme.palette.success.main }}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteApplicant(applicant)}
                                                        sx={{ color: theme.palette.error.main }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Desktop Pagination */}
            {!isMobile && pagination && pagination.last_page > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={pagination.last_page}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Mobile Pagination */}
            {isMobile && pagination && pagination.last_page > 1 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={pagination.last_page}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton={false}
                        showLastButton={false}
                        size="small"
                        siblingCount={0}
                    />
                </Box>
            )}

            {/* Mobile Results Summary */}
            {isMobile && pagination && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} applicants
                    </Typography>
                </Box>
            )}

            {/* Job Applicant Form Dialog */}
            <JobApplicantForm
                open={openForm}
                onClose={() => {
                    setOpenForm(false);
                    setSelectedApplicant(null);
                }}
                onSuccess={() => {
                    setOpenForm(false);
                    setSelectedApplicant(null);
                    loadJobApplicants();
                }}
            />

            {/* Job Applicant View Dialog */}
            <JobApplicantView
                open={openView}
                applicant={selectedApplicant}
                onClose={() => {
                    setOpenView(false);
                    setSelectedApplicant(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete Job Applicant"
                message={`Are you sure you want to delete "${applicantToDelete?.name}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => {
                    setDeleteDialog(false);
                    setApplicantToDelete(null);
                }}
            />
        </Box>
    );
};

export default JobApplicants;
